import { useState } from 'react'
import { Badge } from '@/components/ui'
import { cn } from '@/lib/cn'

export interface RulesSpell {
  id: string
  name: string
  level: number
  school: string
  castingTime: string
  range: string
  components: string
  duration: string
  concentration: boolean
  ritual: boolean
  description: string
  classes: string[]
}

/**
 * Schools are identified by a small coloured dot, not by the card's border.
 * Tinting the whole edge made an Evocation card read as an error and an
 * Abjuration card read as success once the palette became semantic.
 */
const SCHOOL_DOT: Record<string, string> = {
  Abjuration: 'bg-sky-400', Conjuration: 'bg-violet-400',
  Divination: 'bg-amber-300', Enchantment: 'bg-pink-400',
  Evocation: 'bg-orange-400', Illusion: 'bg-fuchsia-400',
  Necromancy: 'bg-emerald-400', Transmutation: 'bg-teal-300',
}

export const levelLabel = (level: number): string =>
  level === 0 ? 'Cantrip' : `${level}${['th', 'st', 'nd', 'rd'][level] ?? 'th'} level`

export function SpellCard({
  spell, action,
}: {
  spell: RulesSpell
  /** Optional control rendered on the card front, e.g. "add to my spells". */
  action?: React.ReactNode
}) {
  const [flipped, setFlipped] = useState(false)

  return (
    <div className="flip-scene h-64">
      <div className="flip-inner h-full w-full" data-flipped={flipped}>
        <div
          className="flip-face panel panel-lit flex h-full w-full flex-col p-4"
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-base leading-tight">{spell.name}</h3>
            <Badge tone={spell.level === 0 ? 'neutral' : 'arcane'}>{levelLabel(spell.level)}</Badge>
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-ink-500">
            <span aria-hidden className={cn('size-1.5 rounded-full', SCHOOL_DOT[spell.school] ?? 'bg-ink-500')} />
            {spell.school}
          </p>

          <dl className="mt-3 space-y-1 text-xs">
            <Row label="Casting" value={spell.castingTime} />
            <Row label="Range" value={spell.range} />
            <Row label="Duration" value={spell.duration} />
            <Row label="Components" value={spell.components} />
          </dl>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {spell.concentration && <Badge tone="gold">Concentration</Badge>}
            {spell.ritual && <Badge tone="moss">Ritual</Badge>}
          </div>

          <div className="mt-auto flex items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => setFlipped(true)}
              className="rounded-lg border border-ink-600 px-2.5 py-1 text-xs text-ink-300 transition-colors hover:border-gold-600 hover:text-gold-400"
            >
              What it does →
            </button>
            {action}
          </div>
        </div>

        <div className="flip-face flip-back panel panel-lit flex flex-col p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-sm leading-tight text-gold-400">{spell.name}</h3>
            <button
              type="button"
              onClick={() => setFlipped(false)}
              aria-label={`Close ${spell.name}`}
              className="shrink-0 rounded-md px-1.5 text-ink-500 transition-colors hover:text-ink-200"
            >
              ✕
            </button>
          </div>
          <p className="mt-2 overflow-y-auto text-[13px] leading-relaxed text-ink-300">
            {spell.description}
          </p>
          {spell.classes.length > 0 && (
            <p className="mt-auto pt-2 text-[11px] capitalize text-ink-500">
              {spell.classes.join(' · ')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-ink-500">{label}</dt>
      <dd className="min-w-0 flex-1 truncate text-ink-300">{value}</dd>
    </div>
  )
}
